import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Contratos from '../../services/Contratos';
import Loading from '../../components/Loading';
import { SweetAlert } from '../../components/sweetalert';
import { URL as URL1 } from '../../config/url';
import { useAuth } from '../../context/auth';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import TableList from '../../components/TableComponents/TableList';
import TableHeadRowCenter from '../../components/TableComponents/TableHeadRowCenter';
import TableHeadRowStart from '../../components/TableComponents/TableHeadRowStart';
import TableBodyRowCenter from '../../components/TableComponents/TableBodyRowCenter';
import TableBodyRowStart from '../../components/TableComponents/TableBodyRowStart';
import { formatMoney, formatMoneySemR$ } from '../../utilities/formatMoneyValue';
import { dataAtualFormatadaDDMMYYYY } from '../../utilities/dataAtualFormatadaDDMMYYYY';

// Mais recentes primeiro: pela data de adesão (texto ISO) e, no mesmo dia, pelo código maior
const newestFirst = (a, b) => b.adesao_data.localeCompare(a.adesao_data) || (b.codigo - a.codigo);

// Aba "Contrato" do cadastro do paciente: todos os contratos dele, sem os filtros de data e de doutor da lista.
// A página monta a aba só quando ela é aberta (mountOnEnter), e a busca acontece nessa hora
const TabContratos = ({ pacienteId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreateContrato = Boolean(user?.features?.includes('create:contrato'));
  const [contratos, setContratos] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [totalValor, setTotalValor] = useState('0,00');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    Contratos.listAll(1, { paciente_id: pacienteId })
      .then((data) => {
        setContratos([...data.data.data].sort(newestFirst));
        setTotalRegistros(data.data.paging.total_records);
        setTotalValor(formatMoneySemR$(data.data.analytics.total_contrato));
      })
      .catch((error) => {
        setFailed(true);
        SweetAlert.error(error?.response?.data?.message ?? error?.message, 'Contratos');
      })
      .finally(() => setLoading(false));
  }, [pacienteId]);

  // O mesmo "Visualizar" da lista de contratos: abre o PDF em outra aba do navegador
  function viewContrato(id) {
    setLoadingPdf(true);

    Contratos.listIdPDF(id)
      .then((dados) => {
        const pdfBlob = new Blob([dados.data], { type: 'application/pdf' });
        window.open(URL.createObjectURL(pdfBlob), '_blank');
      })
      .catch(async (error) => {
        // Com responseType 'blob', a mensagem de erro do backend também chega como Blob
        let message = error?.response?.data?.message ?? error?.message;
        if (error?.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          try {
            message = JSON.parse(text).message;
          } catch (e) {
            message = text;
          }
        }
        SweetAlert.error(message || 'Erro desconhecido', 'Contrato');
      })
      .finally(() => setLoadingPdf(false));
  }

  if (loading) return <div className='p-3'><LoadingSkeleton /></div>;

  // Sem a lista, a aba avisa em vez de mostrar "Sem registros"
  if (failed) return <small className='d-block p-3 text-danger'>Não foi possível carregar os contratos. Recarregue a página.</small>;

  // O cadastro abre com o paciente preenchido e, depois de salvar, volta para esta aba
  const newContratoButton = canCreateContrato ?
    <div className='d-flex justify-content-end px-3 pt-3'>
      <Button variant='success' className='col-12 col-md-auto' onClick={() => navigate(URL1.contrato_cadastro + '?paciente_id=' + pacienteId)}>+ Novo contrato</Button>
    </div> : '';

  if (contratos.length === 0) {
    return (
      <>
        {newContratoButton}
        <div className='d-flex justify-content-center py-2'>
          <h4>Sem registros</h4>
        </div>
      </>
    );
  }

  return (
    <>
      {loadingPdf ? <Loading /> : ''}

      {newContratoButton}

      <TableList>
        <thead>
          <tr>
            <TableHeadRowCenter text='Código' />
            <TableHeadRowCenter text='Data de adesão' />
            <TableHeadRowStart text='Prontuário' />
            <TableHeadRowCenter text='Valor' />
            <TableHeadRowCenter text='Ações' />
          </tr>
        </thead>

        <tbody>
          {contratos.map((contrato) => (
            <tr key={contrato.id} className='trBody'>
              <TableBodyRowCenter text={contrato.codigo} />
              {/* adesao_data chega como '2026-10-01T00:00:00.000Z': a data sai da parte UTC, sem voltar um dia em UTC-3 */}
              <TableBodyRowCenter text={dataAtualFormatadaDDMMYYYY(contrato.adesao_data)} />
              <TableBodyRowStart text={contrato.prontuario?.titulo} />
              <TableBodyRowCenter text={formatMoney(contrato.valor_total)} />

              <td className='tableRowCenter'>
                <div className='buttonContainer'>
                  <Button type='button' variant='outline-secondary' className='m-0 p-0 px-2' onClick={() => navigate(URL1.contrato_cadastro + '/' + contrato.id)}>Editar</Button>
                  <Button type='button' variant='outline-danger' className='m-0 p-0 px-2' onClick={() => viewContrato(contrato.id)} disabled={loadingPdf}>Visualizar</Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </TableList>

      <div className='d-flex justify-content-between px-3 pb-3 flex-column flex-md-row gap-2'>
        <span>Total: {totalRegistros}</span>
        <span>Valor total: R$ {totalValor}</span>
      </div>
    </>
  );
};

export default TabContratos;
